import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { invalidateInventory } from '../lib/hooks';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Spinner } from './ui';

export interface RemovalSubjects {
  assetIds?: string[];
  personIds?: string[];
  buildingIds?: string[];
  roomIds?: string[];
}

interface RemovalRow {
  id: string;
  label: string;
  counts: Record<string, number>;
  /** Why this one can't go: a place that still has devices or carts in it. */
  blocked?: string;
}

interface RemovalPreview {
  assets: RemovalRow[];
  people: RemovalRow[];
  places: RemovalRow[];
  missing: number;
  total: number;
  kept: string[];
}

/** Plain words for the history a record carries, in the order they're worth reading. */
const COUNT_LABEL: Record<string, string> = {
  checkouts: 'checkout',
  workOrders: 'ticket',
  repairs: 'repair',
  incidents: 'incident',
  damageInvoices: 'damage invoice',
  verifications: 'verification',
  assetVerifications: 'verification',
  locationPoints: 'location point',
  loginEvents: 'sign-in',
  rooms: 'room',
  enrollments: 'class enrolment',
  assets: 'device in it now',
  carts: 'cart kept here',
  people: 'person responsible',
  locationPeriods: 'device stay',
  facilityAssets: 'piece of equipment',
  accessPoints: 'access point',
};

const describe = (counts: Record<string, number>) => {
  const parts = Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => `${count} ${COUNT_LABEL[key] ?? key}${count === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' · ') : 'Nothing else recorded';
};

/** Whether the signed-in account is Edvora staff, who are the only ones offered this. */
export const useIsEdvoraStaff = () => !!useAuth().me?.user.isPlatformAdmin;

/**
 * Edvora staff removing a school's devices or people for good, with their history. It counts everything first, asks
 * why, and says what stays behind. A school's own administrators never see this: their delete refuses anything with
 * history on purpose.
 */
export function RemoveRecordsDialog({
  open,
  onClose,
  subjects,
  onRemoved,
}: {
  open: boolean;
  onClose(): void;
  subjects: RemovalSubjects;
  /** After everything is gone: close a page that was showing one of them, or clear a selection. */
  onRemoved?(): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const body = {
    assetIds: subjects.assetIds ?? [],
    personIds: subjects.personIds ?? [],
    buildingIds: subjects.buildingIds ?? [],
    roomIds: subjects.roomIds ?? [],
  };
  const key = [...body.assetIds, ...body.personIds, ...body.buildingIds, ...body.roomIds].join(',');

  const preview = useQuery({
    queryKey: ['removals', 'preview', key],
    queryFn: () => api.post<RemovalPreview>('/removals/preview', body),
    enabled: open && key.length > 0,
    staleTime: 0,
    gcTime: 0,
  });

  const remove = useMutation({
    mutationFn: () => api.post<{ assets: number; people: number; places: number; history: number }>('/removals', { ...body, reason: reason.trim() }),
    onSuccess: (result) => {
      invalidateInventory(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['people'] });
      void queryClient.invalidateQueries({ queryKey: ['buildings'] });
      const removed = [
        result.assets && `${result.assets} device${result.assets === 1 ? '' : 's'}`,
        result.people && `${result.people} ${result.people === 1 ? 'person' : 'people'}`,
        result.places && `${result.places} place${result.places === 1 ? '' : 's'}`,
      ]
        .filter(Boolean)
        .join(' and ');
      toast.success(`Removed ${removed}, with ${result.history} record${result.history === 1 ? '' : 's'} of their history`);
      setReason('');
      onClose();
      onRemoved?.();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const rows = [...(preview.data?.assets ?? []), ...(preview.data?.people ?? []), ...(preview.data?.places ?? [])];
  const held = rows.filter((row) => row.blocked);
  const ready = rows.length > 0 && held.length === 0 && reason.trim().length >= 3;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Remove from this school, for good"
      description="Edvora only. This can’t be undone - the school’s own administrators retire records instead."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={remove.isPending} disabled={!ready} onClick={() => remove.mutate()}>
            Remove permanently
          </Button>
        </>
      }
    >
      {preview.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : preview.error ? (
        <Alert tone="red">{errorMessage(preview.error)}</Alert>
      ) : (
        <div className="space-y-4">
          {rows.length === 0 ? (
            <Alert tone="amber">Nothing here to remove. These records may already be gone.</Alert>
          ) : (
            <>
              <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
                <p className="flex items-center gap-1.5 text-sm font-medium text-red-900">
                  <ShieldAlert className="size-4" /> {rows.length === 1 ? 'This record' : `These ${rows.length} records`} and {preview.data!.total - rows.length} pieces of
                  their history
                </p>
                <ul className="mt-2 space-y-1.5">
                  {rows.map((row) => (
                    <li key={row.id} className="text-sm">
                      <span className="font-medium text-slate-900">{row.label}</span>
                      <span className="block text-xs text-slate-600">{describe(row.counts)}</span>
                      {row.blocked && <span className="block text-xs font-medium text-red-800">{row.blocked}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              {held.length > 0 && (
                <Alert tone="red">
                  {held.length === 1 ? 'That place still holds something.' : `${held.length} of these still hold something.`} Move the devices and carts out, then
                  try again.
                </Alert>
              )}
              {preview.data!.missing > 0 && (
                <Alert tone="amber">
                  {preview.data!.missing} of them {preview.data!.missing === 1 ? "isn't" : "aren't"} in this school and will be left alone.
                </Alert>
              )}
              <ul className="list-disc space-y-1 pl-5 text-xs text-slate-500">
                {preview.data!.kept.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
              <Field label="Why are these being removed?" required hint="Kept in the school’s audit log with what was removed.">
                <Input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="e.g. Imported twice from the wrong file" />
              </Field>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
